import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { strengthLabel, MEMORY_TYPE_LABEL, PROVENANCE_LABEL } from "@/lib/memory-vocab";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type Row = {
  id: string;
  content: string;
  memory_type: string;
  memory_layer: string;
  confidence: number | null;
  group_id: string | null;
  provenance_kind: string | null;
  reinforced_by_count: number | null;
  last_accessed_at: string | null;
  created_at: string;
};

export default function MemoryList({ layers, emptyHint }: { layers: string[]; emptyHint: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["memory-list", layers],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consolidated_memories")
        .select("id, content, memory_type, memory_layer, confidence, group_id, provenance_kind, reinforced_by_count, last_accessed_at, created_at")
        .in("memory_layer", layers)
        .eq("active", true)
        .eq("quarantined", false)
        .order("confidence", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  if (isLoading) return <Skeleton className="h-64" />;
  if (!data?.length) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground text-sm">{emptyHint}</CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-3">
      {data.map(m => {
        const s = strengthLabel(m.confidence);
        return (
          <Link key={m.id} to={`/memory/${m.id}`} className="block">
            <Card className="hover:border-primary transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="text-sm font-medium line-clamp-2">{m.content}</CardTitle>
                  <div className="flex items-center gap-1 shrink-0">
                    {m.reinforced_by_count && m.reinforced_by_count > 0 ? (
                      <Badge variant="secondary" className="text-xs">+{m.reinforced_by_count} reforços</Badge>
                    ) : null}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                  <Badge variant="outline">{MEMORY_TYPE_LABEL[m.memory_type] || m.memory_type}</Badge>
                  {m.provenance_kind && (
                    <span title={PROVENANCE_LABEL[m.provenance_kind]}>
                      {PROVENANCE_LABEL[m.provenance_kind]}
                    </span>
                  )}
                  {m.group_id && <span title="Visível apenas neste grupo">• grupo</span>}
                  <span>• criada {formatDistanceToNow(new Date(m.created_at), { locale: ptBR, addSuffix: true })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs w-20 text-muted-foreground">{s.label}</span>
                  <Progress value={s.pct} className="h-1.5 flex-1" />
                  <span className="text-xs text-muted-foreground w-10 text-right">{s.pct}%</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
